import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { IAgentBuild, ILicense } from 'interfaces/license.interface';
import { requestAgentBuild } from 'requests/license.request';

import Input from 'components/input/input.component';
import { Checkbox } from 'components/checkbox/checkbox.component';
import Button from 'components/button/button.component';
import Loader from 'components/loader/loader.component';
import styles from '../licenses.module.scss';

interface IProps {
  license: ILicense;
  releaseVersion?: string;
  onSuccess: (build: IAgentBuild) => void;
}

/**
 * Confirms the customer's server URL before an installer is stamped.
 *
 * <p>The URL is compiled into what the customer's whole estate will report to, and it is
 * baked into the MSI file rather than passed at install time - so it cannot be corrected
 * after the fact without issuing a new installer. That is worth one deliberate confirmation
 * step, prefilled when the licence already knows the address.
 */
const GenerateAgentModal: React.FC<IProps> = ({ license, releaseVersion, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    backendUrl: Yup.string()
      .trim()
      .required(t('validation.required') as string)
      // The scheme stays optional here: a bare host or IP is accepted and the server
      // prefixes http:// for it. Rejecting it would just make the operator retype what
      // the customer sent them, which is an IP far more often than a full URL.
      .matches(/^(https?:\/\/)?[A-Za-z0-9._-]+(:\d+)?\/?$/, t('validation.invalidUrl') as string),
  });

  const formik = useFormik<{ backendUrl: string; extensionAutoInstall: boolean }>({
    // Ticked by default: forcing the extension is the normal deployment, and unticking it
    // is the deliberate exception for a customer who manages extensions themselves.
    initialValues: {
      backendUrl: license.backendUrl ?? '',
      extensionAutoInstall: true,
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      void formik.setFieldTouched('backendUrl', true, true);
      return;
    }

    setLoading(true);
    try {
      const build = await requestAgentBuild(
        license.id,
        formik.values.backendUrl.trim(),
        formik.values.extensionAutoInstall,
      );
      toast.success(t('licenses.agentRequested'));
      onSuccess(build);
    } catch (err: any) {
      const reason = err?.response?.data;
      toast.error(
        typeof reason === 'string' && reason ? reason : (t('licenses.agentRequestError') as string),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Loader loading={loading}>
      <FormikProvider value={formik}>
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.licenses__agentModal}>
            <dl className={styles.licenses__agentSummary}>
              <dt>{t('licenses.clientId')}</dt>
              <dd>{license.clientId}</dd>
              <dt>{t('licenses.agentRelease')}</dt>
              <dd>{releaseVersion ?? t('licenses.noRelease')}</dd>
            </dl>

            <Input
              formik={formik}
              name="backendUrl"
              value={formik.values.backendUrl}
              label={t('licenses.backendUrl')}
              placeholder="http://10.100.67.70"
              onChange={({ target: { value } }) =>
                formik.setFieldValue('backendUrl', value, true)
              }
            />

            <p className={styles.licenses__agentHint}>{t('licenses.agentUrlHint')}</p>

            <Checkbox
              name="extensionAutoInstall"
              label={t('licenses.extensionAutoInstall') as string}
              checked={formik.values.extensionAutoInstall}
              onChange={({ target: { checked } }) =>
                formik.setFieldValue('extensionAutoInstall', checked, false)
              }
            />

            <p className={styles.licenses__agentHint}>
              {t('licenses.extensionAutoInstallHint')}
            </p>
          </div>

          <div className="modal__footer">
            <Button type="submit" primary disabled={!releaseVersion}>
              {t('licenses.generateAgent')}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </Loader>
  );
};

export default GenerateAgentModal;
